import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagicWand, SquaresFour, File, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";
import { PageHeader } from "@/components/layout/AppShell";
import { AgentAvatar } from "@/components/agent/AgentCard";
import { api, errMsg } from "@/lib/api";
import { useMeta } from "@/lib/useMeta";

const IDEAS = [
  "Un asistente para mi clínica dental que responda dudas de tratamientos, precios orientativos y ayude a pedir cita.",
  "Un agente inmobiliario que cualifique compradores, recomiende propiedades y agende visitas.",
  "Un experto en nutrición deportiva que cree planes de comidas semanales según objetivos.",
];

export default function NewAgent() {
  const navigate = useNavigate();
  const { data: meta } = useMeta();
  const [idea, setIdea] = useState("");
  const [busy, setBusy] = useState(null);

  const create = async (payload, key) => {
    setBusy(key);
    try {
      const { data } = await api.post("/agents", payload);
      toast.success("Agente creado");
      navigate(`/agents/${data.agent_id}/edit`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  const generate = async () => {
    if (idea.trim().length < 10) return toast.error("Describe tu agente con algo más de detalle");
    setBusy("ai");
    try {
      const { data: bp } = await api.post("/ai/blueprint", { description: idea });
      await create({ ...bp, icon: "Sparkle", color: "#22D3EE" }, "ai");
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(null);
    }
  };

  const fromTemplate = (t) => {
    const { template_id, price, pricing_type, ...rest } = t;
    create(rest, template_id);
  };

  return (
    <div>
      <PageHeader eyebrow="Nuevo agente" title="¿Cómo quieres empezar?" subtitle="Tres caminos. Todos editables al 100% después." />
      <div className="space-y-12 px-6 py-10 lg:px-12">
        <section className="panel relative overflow-hidden" data-testid="new-agent-ai-section">
          <div className="grid-bg absolute inset-0 opacity-50" />
          <div className={`relative p-8 ${busy === "ai" ? "thinking-beam" : ""}`}>
            <div className="flex items-center gap-3"><MagicWand size={22} weight="duotone" className="text-cyan-400" /><p className="eyebrow !text-cyan-400">Diseño asistido por IA</p></div>
            <h2 className="mt-4 font-display text-2xl font-bold sm:text-3xl">Describe tu agente. Nosotros lo construimos.</h2>
            <textarea value={idea} onChange={(e) => setIdea(e.target.value)} rows={4} className="field mt-6 !text-base" placeholder="Ej.: Un agente de ventas para mi software de facturación que cualifique leads y resuelva objeciones…" data-testid="new-agent-idea-input" />
            <div className="mt-3 flex flex-wrap gap-2">
              {IDEAS.map((i, n) => <button key={n} onClick={() => setIdea(i)} className="border border-white/10 px-3 py-1.5 text-left text-xs text-zinc-400 hover:border-white/30 hover:text-white" data-testid={`new-agent-idea-${n}`}>{i.slice(0, 58)}…</button>)}
            </div>
            <button onClick={generate} disabled={!!busy} className="btn-primary mt-6 !px-6 !py-3" data-testid="new-agent-generate-button">
              {busy === "ai" ? "Construyendo agente…" : <>Generar con IA <ArrowRight size={16} weight="bold" /></>}
            </button>
          </div>
        </section>

        <section>
          <div className="mb-5 flex items-center gap-3"><SquaresFour size={20} className="text-blue-500" /><h2 className="font-display text-xl font-bold">Plantillas profesionales</h2></div>
          <div className="grid gap-px border border-white/10 bg-white/10 sm:grid-cols-2 xl:grid-cols-4">
            {(meta?.templates || []).map((t, i) => (
              <button key={t.template_id} disabled={!!busy} onClick={() => fromTemplate(t)} className="reveal group flex flex-col items-start bg-[#080808] p-6 text-left hover:bg-[#111] disabled:opacity-50" style={{ animationDelay: `${i * 40}ms`, transition: "background-color 200ms" }} data-testid={`template-${t.template_id}`}>
                <AgentAvatar icon={t.icon} color={t.color} />
                <p className="mt-4 text-[11px] uppercase tracking-widest text-zinc-500">{t.category}</p>
                <p className="mt-1 font-display text-lg font-bold">{t.name}</p>
                <p className="mt-1 text-sm text-zinc-500">{t.tagline}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-blue-400 opacity-0 group-hover:opacity-100" style={{ transition: "opacity 200ms" }}>{busy === t.template_id ? "Creando…" : "Usar plantilla"} <ArrowRight size={12} /></span>
              </button>
            ))}
          </div>
        </section>

        <section>
          <button disabled={!!busy} onClick={() => create({ name: "Nuevo agente", icon: "Robot", color: "#2563EB" }, "blank")} className="group flex w-full items-center justify-between border border-dashed border-white/15 p-6 text-left hover:border-blue-500" style={{ transition: "border-color 200ms" }} data-testid="new-agent-blank-button">
            <div className="flex items-center gap-4"><File size={22} className="text-zinc-400" /><div><p className="font-display text-lg font-bold">Empezar en blanco</p><p className="text-sm text-zinc-500">Control total desde la primera línea.</p></div></div>
            <ArrowRight size={18} className="text-zinc-500 group-hover:text-white" />
          </button>
        </section>
      </div>
    </div>
  );
}
